import React from 'react';
import { formatCurrency } from '../../../utils/assessmentResults/formatters';
import { calculateTotalSavings, calculateAveragePayback, calculateSavingsPercentage, getRecommendationsByCategory } from '../../../utils/assessmentResults/calculations';
import { MetricCard } from '../Components/MetricCard';

/**
 * Executive Summary Tab Component
 */
export const ExecutiveSummaryTab = ({ assessment, recommendations = [] }) => {
  const safeRecommendations = recommendations || [];
  const totalSavings = calculateTotalSavings(safeRecommendations);
  const averagePayback = calculateAveragePayback(safeRecommendations);

  // Show assessment overview even without recommendations
  if (safeRecommendations.length === 0) {
    return (
      <div className="space-y-6">
        {/* Assessment Overview - Always show */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Assessment Overview</h3>
          {assessment ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold text-gray-800 dark:text-gray-200 mb-3">Facility Information</h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Facility Name:</span>
                    <span className="font-medium text-gray-900 dark:text-white">{assessment.facility_name || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Industry:</span>
                    <span className="font-medium text-gray-900 dark:text-white">{assessment.industry_name || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Assessment Type:</span>
                    <span className="font-medium text-gray-900 dark:text-white">{assessment.assessment_type || assessment.type || 'ESA'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Completion Date:</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {assessment.completion_date ? new Date(assessment.completion_date).toLocaleDateString() : 'N/A'}
                    </span>
                  </div>
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-gray-800 dark:text-gray-200 mb-3">Energy Information</h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Annual Energy Spend:</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      ${(assessment.annual_energy_spend || 0).toLocaleString()}
                    </span>
                  </div>
                  {assessment.estimated_savings > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Estimated Savings:</span>
                      <span className="font-medium text-emerald-600 dark:text-emerald-400">
                        ${assessment.estimated_savings.toLocaleString()}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <p className="text-gray-600 dark:text-gray-400">Loading assessment data...</p>
          )}
        </div>

        {/* Recommendations Status */}
        <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-xl p-6">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Recommendations Status</h3>
          <p className="text-gray-700 dark:text-gray-300">
            Recommendations are being generated. This process analyzes your assessment responses and generates 
            personalized energy efficiency opportunities. Please check back in a few moments or refresh the page.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 dark:bg-slate-800">
      {/* Key Findings Dashboard */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <MetricCard
          value={formatCurrency(totalSavings)}
          label="Total Annual Savings Potential"
          subtitle={`Based on ${safeRecommendations.length} recommendations`}
          color="emerald"
        />
        <MetricCard
          value={`${averagePayback.toFixed(1)} years`}
          label="Average Payback Period"
          subtitle="Weighted across all measures"
          color="blue"
        />
        <MetricCard
          value={safeRecommendations.length}
          label="Implementation Opportunities"
          subtitle="Prioritized by impact & feasibility"
          color="purple"
        />
      </div>

      {/* Assessment Overview */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Assessment Overview</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-semibold text-gray-800 dark:text-gray-200 mb-3">Energy Spend Analysis</h4>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Annual Energy Spend:</span>
                <span className="font-medium text-gray-900 dark:text-white">
                  {formatCurrency(assessment?.annual_energy_spend || 0)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Potential Savings:</span>
                <span className="font-medium text-emerald-600 dark:text-emerald-400">
                  {totalSavings > 0 ? `${calculateSavingsPercentage(totalSavings, assessment?.annual_energy_spend).toFixed(1)}%` : 'N/A'}
                </span>
              </div>
            </div>
          </div>
          <div>
            <h4 className="font-semibold text-gray-800 dark:text-gray-200 mb-3">Recommendation Categories</h4>
            <div className="space-y-2">
              {['Low-cost', 'Medium-cost', 'High-cost'].map((category) => {
                const categoryKey = category.toLowerCase().replace('-', '_');
                const count = getRecommendationsByCategory(safeRecommendations, categoryKey).length;
                return (
                  <div key={category} className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">{category}:</span>
                    <span className="font-medium text-gray-900 dark:text-white">{count} measures</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Professional Analysis Complete Badge */}
      <div className="bg-gradient-to-r from-emerald-50 to-blue-50 dark:from-emerald-900/30 dark:to-blue-900/30 rounded-xl p-6 border border-emerald-200 dark:border-emerald-700">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Professional Analysis Complete</h3>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              This consultant-led assessment includes all professional features:
            </p>
            <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
              <li className="flex items-center gap-2">
                <span className="text-emerald-500 dark:text-emerald-400">✓</span>
                Detailed ARC implementation guides for each recommendation
              </li>
              <li className="flex items-center gap-2">
                <span className="text-emerald-500 dark:text-emerald-400">✓</span>
                Step-by-step installation procedures and technical specifications
              </li>
              <li className="flex items-center gap-2">
                <span className="text-emerald-500 dark:text-emerald-400">✓</span>
                Industry benchmarking and performance comparison
              </li>
              <li className="flex items-center gap-2">
                <span className="text-emerald-500 dark:text-emerald-400">✓</span>
                Comprehensive implementation roadmap with timelines
              </li>
            </ul>
          </div>
          <div className="text-center">
            <div className="bg-emerald-600 dark:bg-emerald-700 text-white px-6 py-3 rounded-lg font-semibold mb-2">
              ✓ All Features Unlocked
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">Consultant-Led Session</p>
          </div>
        </div>
      </div>
    </div>
  );
};

